import { isActionable, SYNC_STATUS, type SyncStatus } from './status.js';
import { setParams } from './urlParams.js';

/** `all`: every row. `needs_attention`: drift or missing in any environment. */
export type RowSegment = 'all' | 'needs_attention';
/** `environment` focuses the grid on one environment's column and scopes the segment, the status and the attention count to it. */
export type RowFilter = { search: string; segment: RowSegment; status: SyncStatus | null; environment: string | null };
export const EMPTY_FILTER: RowFilter = { search: '', segment: 'all', status: null, environment: null };

/** Whether fewer ROWS are shown. An environment alone hides columns, never rows, so it does not count. */
export const isFiltered = (f: RowFilter): boolean => f.search !== '' || f.segment !== 'all' || f.status !== null;

/**
 * The grid filter's URL: `?q=&view=attention&status=drift&env=staging`, so a filtered grid survives a
 * reload, the back button and a pasted link, and a page linking into a grid says what it wants
 * in the query rather than handing it over in navigation state. Read is total: an unknown
 * `view` or `status` reads as no filter rather than throwing.
 */
const SEARCH_PARAM = 'q';
const VIEW_PARAM = 'view';
const STATUS_PARAM = 'status';
const ENV_PARAM = 'env';
const ROW_PARAMS = [SEARCH_PARAM, VIEW_PARAM, STATUS_PARAM, ENV_PARAM] as const;

export const readRowFilter = (params: URLSearchParams): RowFilter => {
    const status = params.get(STATUS_PARAM);
    return {
        search: params.get(SEARCH_PARAM) ?? '',
        segment: params.get(VIEW_PARAM) === 'attention' ? 'needs_attention' : 'all',
        status: status && Object.hasOwn(SYNC_STATUS, status) ? (status as SyncStatus) : null,
        environment: params.get(ENV_PARAM)?.trim() || null,
    };
};

/** Empty values and the defaults delete their param, so an unfiltered grid's URL is bare. Every other param is kept. */
export const writeRowFilter = (params: URLSearchParams, patch: Partial<RowFilter>): URLSearchParams =>
    setParams(params, {
        ...('search' in patch ? { [SEARCH_PARAM]: patch.search } : {}),
        ...('segment' in patch ? { [VIEW_PARAM]: patch.segment === 'needs_attention' ? 'attention' : null } : {}),
        ...('status' in patch ? { [STATUS_PARAM]: patch.status } : {}),
        ...('environment' in patch ? { [ENV_PARAM]: patch.environment } : {}),
    });

/** Removes the grid filter's params only — the Games page's `?studio=` is not a row filter and stays. */
export const clearRowFilter = (params: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams(params);
    for (const key of ROW_PARAMS) next.delete(key);
    return next;
};

/**
 * Spelled with conditional spreads so an unfiltered grid's input is structurally `{}`. The
 * environment is sent only with something for it to scope, so it never splits the query cache.
 */
export const queryFilter = (
    f: RowFilter,
): { search?: string; statuses?: SyncStatus[]; onlyActionable?: boolean; environment?: string } => ({
    ...(f.search ? { search: f.search } : {}),
    ...(f.status ? { statuses: [f.status] } : {}),
    ...(f.segment === 'needs_attention' ? { onlyActionable: true } : {}),
    ...(f.environment && (f.status || f.segment === 'needs_attention') ? { environment: f.environment } : {}),
});

/** A grid's URL filtered to one environment's rows with `status`: what an Environments card's count links to. */
export const rowFilterQuery = (environment: string, status: SyncStatus): string =>
    writeRowFilter(new URLSearchParams(), { environment, status }).toString();

type RowLike = { cells: Record<string, { status: SyncStatus }> };

/**
 * Rows on screen whose OWN status is drift or missing — in `environment` when one is picked,
 * otherwise in any — the toolbar's count. A studio's games do not count.
 */
export const needsAttention = (rows: readonly RowLike[], environment?: string | null): number =>
    rows.filter((row) =>
        (environment ? [row.cells[environment]] : Object.values(row.cells)).some((c) => c && isActionable(c.status)),
    ).length;

/**
 * The environment columns the grid draws: only the picked one, so switching environments
 * visibly changes the grid. A picked name no column has (a stale pasted link) shows every
 * column rather than none. Returns the INPUT array itself, not a copy, when nothing is
 * filtered — an unmoved grid's `columns` prop then keeps its identity across renders, which is
 * what lets a memoised row skip re-rendering on an unrelated change.
 */
export const visibleColumns = <C extends { name: string }>(columns: readonly C[], environment: string | null): C[] =>
    environment && columns.some((c) => c.name === environment)
        ? columns.filter((c) => c.name === environment)
        : (columns as C[]);
